import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BaseModalComponent } from 'app/shared/base-modal/base-modal.component';
import { SnackbarService } from 'app/services/snackbar.service';
import {
  MigrationMode,
  ProductMigrationAction,
  ProductMigrationDecision,
  ProductMigrationPreviewItem,
  RelatedProductsMigrationService,
} from 'app/services/related-products-migration.service';

export interface MigrateRelatedProductsDialogData {
  mode: MigrationMode;
  menuSectionId: string;
}

interface PreviewRow {
  item: ProductMigrationPreviewItem;
  action: ProductMigrationAction;
}

@Component({
  selector: 'app-migrate-related-products-dialog',
  templateUrl: './migrate-related-products-dialog.component.html',
  styleUrls: ['./migrate-related-products-dialog.component.scss'],
})
export class MigrateRelatedProductsDialogComponent extends BaseModalComponent implements OnInit {
  readonly Action = ProductMigrationAction;
  loading = true;
  applying = false;
  errorMessage: string | null = null;
  rows: PreviewRow[] = [];
  title = '';

  constructor(
    public override dialogRef: MatDialogRef<MigrateRelatedProductsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MigrateRelatedProductsDialogData,
    private migrationService: RelatedProductsMigrationService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
  ) {
    super(dialogRef);
    const titleKey = data.mode === 'legacy-to-v2'
      ? 'MENU.MIGRATE_RELATED_PRODUCTS_TITLE_LEGACY_TO_V2'
      : 'MENU.MIGRATE_RELATED_PRODUCTS_TITLE_V2_TO_V3';
    this.title = this.translate.instant(titleKey);
  }

  async ngOnInit(): Promise<void> {
    try {
      const items = await this.migrationService.preview(this.data.mode, this.data.menuSectionId);
      this.rows = items.map((item) => ({
        item,
        action: item.hasConflict ? ProductMigrationAction.Skip : ProductMigrationAction.Append,
      }));
    } catch (e: unknown) {
      this.errorMessage = this.formatError(e);
    } finally {
      this.loading = false;
    }
  }

  applyAllWhereTargetEmpty(): void {
    for (const row of this.rows) {
      if (!row.item.hasConflict) {
        row.action = ProductMigrationAction.Append;
      }
    }
  }

  skipAllConflicts(): void {
    for (const row of this.rows) {
      if (row.item.hasConflict) {
        row.action = ProductMigrationAction.Skip;
      }
    }
  }

  selectAllOverwrite(): void {
    for (const row of this.rows) {
      row.action = ProductMigrationAction.Overwrite;
    }
  }

  selectAllSkip(): void {
    for (const row of this.rows) {
      row.action = ProductMigrationAction.Skip;
    }
  }

  async apply(): Promise<void> {
    this.applying = true;
    this.errorMessage = null;
    try {
      const decisions: ProductMigrationDecision[] = this.rows.map((row) => ({
        productId: row.item.productId,
        action: row.action,
      }));
      const result = await this.migrationService.execute(this.data.mode, this.data.menuSectionId, decisions);
      const msg = this.translate.instant('MENU.MIGRATE_RELATED_PRODUCTS_RESULT', {
        applied: result.applied,
        skipped: result.skipped,
        failed: result.failed,
      });
      if (result.failed > 0) {
        this.snackbar.error(msg);
      } else {
        this.snackbar.success(msg);
      }
      this.dialogRef.close({ result });
    } catch (e: unknown) {
      this.errorMessage = this.formatError(e);
    } finally {
      this.applying = false;
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackByProductId(_index: number, row: PreviewRow): string {
    return row.item.productId;
  }

  private formatError(err: unknown): string {
    const e = err as { error?: string; message?: string };
    if (e?.error && typeof e.error === 'string') {
      return e.error;
    }

    if (e?.message) {
      return e.message;
    }

    return this.translate.instant('COMMON.SOME_ERROR');
  }
}
